/**
 * AI CONTEXT: mobile-first - Interactive giveaway entry and management page
 * Journal: J-20260108-01, J-20250809-01, J-20250809-02, J-20250811-02, J-20250811-03, J-20250811-04, J-20250811-05, J-20250811-06, J-20250811-07, J-20250811-08, J-20250811-09, J-20250813-09, J-20250813-10
 * 
 * This page provides:
 * - Real-time countdowns and availability updates
 * - Entry flow with validation and credits handling
 * - Tier-based access and subscriber perks
 * - Consistent image fallbacks via `getAssetUrl`
 * 
 * Architecture decisions:
 * - Source data from `GiveawayContext` backed by API where configured
 * - Keep optimistic UI for entry/swap operations; refresh after server writes
 * - Use toasts for user feedback
 * - Gate giveaways behind FREE registration to align with business flow
 * 
 * Future considerations:
 * - Persist entries via secured REST API (implemented)
 * - Add pagination and filtering
 * - Integrate audit logs for admin review
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { t } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { useGiveaway } from '../contexts/GiveawayContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useCart } from '../contexts/CartContext';
import { useProducts } from '../contexts/ProductContext';
import { Gift, Crown, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';
import S3Image from '../components/S3Image';
import CountdownTimer from '../components/CountdownTimer';
// import GiveawayWheel from '../components/GiveawayWheel';
import GiveawaySlotGrid from '../components/GiveawaySlotGrid';
import ClassifiedPrizeMedia from '../components/ClassifiedPrizeMedia';
// import { useCart } from '../contexts/CartContext';
import { authorizedFetch, isApiConfigured } from '../services/apiClient';
import authService from '../services/auth';
import { setSEO } from '../utils/seo';
import { ensureDate, formatDateTime, calculateEarlyAccessDate, shouldShowClassifiedPrize } from '../utils/date';
import { compareGiveawaysByRecency } from '../utils/giveawaySort';
import { readPendingGiveawayEntry, writePendingGiveawayEntry, clearPendingGiveawayEntry } from '../utils/pendingGiveawayEntry';

// Shipping address validation constants
const SHIPPING_ADDRESS_CACHE_KEY = 'tolbert_shipping_address_status';
const SHIPPING_ADDRESS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const RATE_LIMIT_DURATION = 2000; // 2 seconds between checks
// Gallery component extracted to avoid remounts on parent re-renders (e.g., live clock)
// Keeps internal state stable and prevents image flicker. Adds swipe and arrow nav,
// and a horizontally scrollable thumbnail strip.
const CardImageGallery = React.memo(({ items, alt }) => {
  const [active, setActive] = React.useState(0);
  const safe = Array.isArray(items) && items.length > 0 ? items : ['/files/Logo.png'];
  const main = safe[Math.min(active, safe.length - 1)];
  const startXRef = React.useRef(0);
  const pointerDownRef = React.useRef(false);
  const onPrev = () => setActive(i => Math.max(0, i - 1));
  const onNext = () => setActive(i => Math.min(safe.length - 1, i + 1));

  return (
    <div
      className="relative overflow-hidden"
      onTouchStart={(e) => { if (e.touches && e.touches[0]) startXRef.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const t = e.changedTouches && e.changedTouches[0];
        if (!t) return;
        const dx = t.clientX - startXRef.current;
        if (Math.abs(dx) > 30) {
          if (dx < 0) onNext(); else onPrev();
        }
      }}
      onPointerDown={(e) => { pointerDownRef.current = true; startXRef.current = e.clientX; }}
      onPointerUp={(e) => {
        if (!pointerDownRef.current) return;
        pointerDownRef.current = false;
        const dx = e.clientX - startXRef.current;
        if (Math.abs(dx) > 30) {
          if (dx < 0) onNext(); else onPrev();
        }
      }}
    >
      <S3Image
        s3KeyOrUrl={main}
        alt={alt}
        className="w-full h-48 max-w-full max-h-full object-contain p-4 rounded-t-lg"
        fallback="/files/Logo.png"
      />
      {safe.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous image"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPrev(); }}
            className="flex absolute left-2 top-1/2 -translate-y-1/2 bg-theme-black/50 hover:bg-theme-black/70 text-theme-silver rounded-full w-8 h-8 items-center justify-center z-10"
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Next image"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNext(); }}
            className="flex absolute right-2 top-1/2 -translate-y-1/2 bg-theme-black/50 hover:bg-theme-black/70 text-theme-silver rounded-full w-8 h-8 items-center justify-center z-10"
          >
            ›
          </button>
                  <div className="absolute bottom-2 left-0 right-0 px-2">
            <div
              className="flex gap-2 overflow-x-auto whitespace-nowrap justify-start mx-auto max-w-full scrollbar-thin scrollbar-thumb-theme-silver/30 scrollbar-track-transparent"
              style={{ WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain' }}
            >
              {safe.map((thumb, idx) => (
                <button
                  key={thumb + idx}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActive(idx); }}
                  className={`border rounded ${active === idx ? 'border-custom-orange' : 'border-theme-silver/30'} bg-theme-black/60 flex-shrink-0`}
                  title={`Image ${idx + 1}`}
                >
                  <S3Image s3KeyOrUrl={thumb} alt="thumb" className="w-10 h-10 object-contain" fallback="/files/Logo.png" />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
});

const Giveaways = () => {
  // Toggle to show/hide the giveaway wheel and disclaimer
  const SHOW_WHEEL = false;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const shouldAutoEnterPending = searchParams.get('autoEnter') === '1';
  const { user, isAuthenticated, isAdmin } = useAuth();
  const { addToCart } = useCart();
  const { products } = useProducts();
  const { isUserSubscribed: subscriptionIsUserSubscribed } = useSubscription();
  // Selected giveaway for the global wheel
  const [selectedGiveawayId, setSelectedGiveawayId] = useState(null);
  // AI CONTEXT: ux - Modal for giveaway slot selection
  // Reasoning: Users want a focused modal with just the slot grid when clicking giveaway cards
  // Constraints: Modal should show larger slot grid, cancel button, and enter button only
  // Journal: J-20251226-04
  const [modalGiveaway, setModalGiveaway] = useState(null); // Giveaway object for modal
  const [selectedSlots, setSelectedSlots] = useState([]); // Selected slots in modal
  const [modalSlotsData, setModalSlotsData] = useState(null); // Pre-fetched slots data for modal
  const { 
    giveaways, 
    canViewGiveaway,
    enterGiveaway, 
    getUserCredits,
    issueMonthlyCredit,
    refreshUserCredits
  } = useGiveaway();
  // SlotSelectionModal removed - using inline grid only now
  // State for insufficient credits modal
  const [insufficientCreditsInfo, setInsufficientCreditsInfo] = useState(null); // { availableCredits, selectedSlotsCount, missingCredits, tier, giveaway, selectedSlots }
  // Slot swapping disabled per client request (slots are locked after entry)
  const [currentTime, setCurrentTime] = useState(new Date());
  // const [showCalendar, setShowCalendar] = useState(false); // hidden UI
  // const [selectedDate, setSelectedDate] = useState(null); // hidden UI
  const [serverEntries, setServerEntries] = useState([]);
  const [listTab, setListTab] = useState('all'); // 'all' | 'active' | 'past'
  const [shippingAddressStatus, setShippingAddressStatus] = useState(null); // null | 'complete' | 'incomplete'
  const [lastAddressCheck, setLastAddressCheck] = useState(0); // Rate limiting timestamp
  // Lightweight modal for no-credits case from wheel click
  const [noCreditsInfo, setNoCreditsInfo] = useState(null); // { tier, slot }
  // Lightweight confirmation for reserving a specific slot from the wheel
  const [reserveInfo, setReserveInfo] = useState(null); // { slot }
  // Global UI pause flag to prevent background updates from closing modals
  // const [uiPaused, setUiPaused] = useState(false);
  
  // Leaderboard and product carousel state
  const [leaders, setLeaders] = useState([]);
  // const [myRank, setMyRank] = useState(null); // not used currently
  const [loadingLeaders, setLoadingLeaders] = useState(false);
  const [topEntries, setTopEntries] = useState([]);
  const [loadingTopEntries, setLoadingTopEntries] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const pendingEntryProcessingRef = React.useRef(false);
  const [pendingEntryBanner, setPendingEntryBanner] = useState(null);
  // Pin the giveaway ID when a modal opens so entry always targets the correct giveaway
  const modalGiveawayIdRef = React.useRef(null);
  const modalSlotsFetchGenRef = React.useRef(0);
  
  // Filter products for carousel (limit to 3-4 items)
  // Hide admin-only test products from non-admin customers
  const carouselProducts =
    products
      ?.filter(p => (p.credits > 0 || p.entries > 0) && (!p.adminOnly || isAdmin))
      ?.slice(0, 4) || [];
  const isSubscriber = useMemo(() => {
    if (!user) return false;

    if (typeof subscriptionIsUserSubscribed === 'function') {
      try {
        const derived = subscriptionIsUserSubscribed(user.id || user.email);
        if (typeof derived === 'boolean') {
          return derived;
        }
      } catch (subscriptionError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('🔍 Giveaways: SubscriptionContext.isUserSubscribed threw an error:', subscriptionError);
        }
      }
    }

    return Boolean(user?.isSubscriber);
  }, [subscriptionIsUserSubscribed, user]);

  // Load leaderboard data from API with fallback
  useEffect(() => {
    const loadLeaders = async () => {
      setLoadingLeaders(true);
      try {
        const apiUrl = process.env.REACT_APP_API_BASE_URL;
        
        // Try API first if configured
        if (apiUrl) {
          try {
            const response = await fetch(`${apiUrl}/leaderboard`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              setLeaders(data.leaderboard || []);
              setLoadingLeaders(false);
              return;
            }
          } catch (apiError) {
            console.warn('API leaderboard failed, trying fallback:', apiError);
          }
        }
        
        // Fallback: Use mock data for development/demo
        console.log('Using fallback leaderboard data');
        const fallbackLeaders = [
          {
            displayName: "J**n S***h",
            displayEmail: "j***h@example.com",
            purchaseCount: 3,
            totalCredits: 8
          },
          {
            displayName: "S***h M****r",
            displayEmail: "s***h@example.com", 
            purchaseCount: 2,
            totalCredits: 5
          },
          {
            displayName: "M**e R*******s",
            displayEmail: "m***r@example.com",
            purchaseCount: 1,
            totalCredits: 3
          }
        ];
        setLeaders(fallbackLeaders);
        
      } catch (error) {
        console.error('Failed to load leaderboard:', error);
        setLeaders([]);
      } finally {
        setLoadingLeaders(false);
      }
    };
    
    loadLeaders();
  }, []);

  // Load top entries data from API with fallback
  // AI CONTEXT: leaderboards - Top Entries shows customers with most total entries across all giveaways
  // Reasoning: Client wants to show both purchase leaders and entry leaders in same box
  // Journal: J-20260112-02
  useEffect(() => {
    const loadTopEntries = async () => {
      setLoadingTopEntries(true);
      try {
        const apiUrl = process.env.REACT_APP_API_BASE_URL;
        
        // Try API first if configured
        if (apiUrl) {
          try {
            const response = await fetch(`${apiUrl}/leaderboard/entries`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              setTopEntries(data.leaderboard || []);
              setLoadingTopEntries(false);
              return;
            }
          } catch (apiError) {
            console.warn('API top entries failed, trying fallback:', apiError);
          }
        }
        
        // Fallback: Use mock data for development/demo
        console.log('Using fallback top entries data');
        const fallbackEntries = [
          {
            displayName: "S***h M****r",
            displayEmail: "s***h@example.com",
            totalEntries: 24
          },
          {
            displayName: "J**n S***h",
            displayEmail: "j***h@example.com",
            totalEntries: 18
          },
          {
            displayName: "A**x T*****r",
            displayEmail: "a***t@example.com",
            totalEntries: 15
          }
        ];
        setTopEntries(fallbackEntries);
        
      } catch (error) {
        console.error('Failed to load top entries:', error);
        setTopEntries([]);
      } finally {
        setLoadingTopEntries(false);
      }
    };
    
    loadTopEntries();
  }, []);

  // Real-time clock for countdown timers
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // stopScrollPropagation removed - not needed without SlotSelectionModal

  // Auto-issue monthly credits for subscribers
  useEffect(() => {
    if (isSubscriber) {
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const credits = getUserCredits(user.id);
      
      if (credits.monthlyCreditIssued) {
        const lastMonth = credits.monthlyCreditIssued.substring(0, 7);
        if (lastMonth !== currentMonth) {
          issueMonthlyCredit(user.id);
          toast.success('Monthly credit issued! Check your credits.');
        }
      }
    }
  }, [user, issueMonthlyCredit, getUserCredits, isSubscriber]);

  // Shipping address validation functions
  const validateShippingAddress = (shippingAddress) => {
    if (!shippingAddress) return false;
    
    const requiredFields = ['line1', 'city', 'state', 'postalCode'];
    const missingFields = requiredFields.filter(field => {
      const value = shippingAddress[field];
      return !value || (typeof value === 'string' && value.trim() === '');
    });
    
    return missingFields.length === 0;
  };

  const getShippingAddressFromCache = () => {
    try {
      const cached = localStorage.getItem(SHIPPING_ADDRESS_CACHE_KEY);
      if (!cached) return null;
      
      const { status, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - timestamp < SHIPPING_ADDRESS_CACHE_DURATION) {
        return status;
      }
      
      // Cache expired, remove it
      localStorage.removeItem(SHIPPING_ADDRESS_CACHE_KEY);
      return null;
    } catch (error) {
      console.warn('Error reading shipping address cache:', error);
      localStorage.removeItem(SHIPPING_ADDRESS_CACHE_KEY);
      return null;
    }
  };

  const setShippingAddressCache = (status) => {
    try {
      const cacheData = {
        status,
        timestamp: Date.now()
      };
      localStorage.setItem(SHIPPING_ADDRESS_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Error setting shipping address cache:', error);
    }
  };

  const checkShippingAddressStatus = useCallback(async (forceCheck = false) => {
    const now = Date.now();
    
    // Rate limiting check
    if (!forceCheck && now - lastAddressCheck < RATE_LIMIT_DURATION) {
      console.log('Rate limited: skipping shipping address check');
      return shippingAddressStatus;
    }
    
    setLastAddressCheck(now);
    
    // Check cache first (unless forced)
    if (!forceCheck) {
      const cachedStatus = getShippingAddressFromCache();
      if (cachedStatus !== null) {
        console.log('Using cached shipping address status:', cachedStatus);
        setShippingAddressStatus(cachedStatus);
        return cachedStatus;
      }
    }
    
    // Check database if not in cache or forced
    try {
      console.log('Checking shipping address status from database...');
      const profile = await authorizedFetch('/my/profile', { method: 'GET' });
      
      console.log('🔍 DEBUG: Profile response:', profile);
      console.log('🔍 DEBUG: Shipping address from profile:', profile?.shippingAddress);
      
      if (profile && profile.shippingAddress) {
        const isValid = validateShippingAddress(profile.shippingAddress);
        const status = isValid ? 'complete' : 'incomplete';
        
        console.log('🔍 DEBUG: Validation result:', isValid);
        console.log('🔍 DEBUG: Shipping address status from database:', status);
        setShippingAddressStatus(status);
        setShippingAddressCache(status);
        return status;
      } else {
        console.log('No shipping address found in profile');
        setShippingAddressStatus('incomplete');
        setShippingAddressCache('incomplete');
        return 'incomplete';
      }
    } catch (error) {
      console.warn('Error checking shipping address status:', error);
      // On error, assume incomplete to be safe
      setShippingAddressStatus('incomplete');
      return 'incomplete';
    }
  }, [shippingAddressStatus, lastAddressCheck]);

  // Load shipping address status on component mount
  useEffect(() => {
    if (user && isApiConfigured()) {
      checkShippingAddressStatus();
    }
  }, [user, checkShippingAddressStatus]);

  // Refresh shipping address status when page becomes visible (user returns from profile update)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user && isApiConfigured()) {
        // Force refresh when page becomes visible
        checkShippingAddressStatus(true);
      }
    };

    // Listen for storage changes (when user updates profile in another tab)
    const handleStorageChange = (e) => {
      if (e.key === SHIPPING_ADDRESS_CACHE_KEY && user && isApiConfigured()) {
        console.log('Storage change detected, clearing cache and refreshing status');
        // Clear the cache and force a fresh check
        localStorage.removeItem(SHIPPING_ADDRESS_CACHE_KEY);
        checkShippingAddressStatus(true);
      }
    };

    // Listen for custom shipping address update events
    const handleShippingAddressUpdate = (e) => {
      if (user && isApiConfigured()) {
        console.log('Shipping address update event received, refreshing status');
        // Clear the cache and force a fresh check
        localStorage.removeItem(SHIPPING_ADDRESS_CACHE_KEY);
        checkShippingAddressStatus(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('shippingAddressUpdated', handleShippingAddressUpdate);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('shippingAddressUpdated', handleShippingAddressUpdate);
    };
  }, [user, checkShippingAddressStatus]);

  // AI CONTEXT: data-fetching - Load and refresh server entries for authenticated user
  // Reasoning: User entries need to be displayed correctly in slot grids with green highlighting
  // Constraints: Must refresh after successful entry to show newly entered slots
  // Journal: J-20251226-02
  
  const refreshServerEntries = useCallback(async () => {
    if (!user || !isApiConfigured()) return;
    
    // Ensure user is authenticated before making API calls
    try {
      const { isAuthenticated } = await authService.checkAuthStatus();
      if (!isAuthenticated) {
        console.log('User not authenticated, skipping entries API call');
        return;
      }
    } catch (error) {
      console.warn('Auth status check failed:', error);
      return;
    }
    
    try {
      const data = await authorizedFetch('/my/entries', { method: 'GET' });
      if (Array.isArray(data)) {
        setServerEntries(data);
      }
    } catch (error) {
      if (error.message.includes('Authentication required')) {
        console.warn('Authentication required for entries API');
      } else {
        console.warn('Failed to load entries:', error);
      }
      // ignore, fallback to giveaway.entries
    }
  }, [user]);

  // Load server entries for the authenticated user
  useEffect(() => {
    refreshServerEntries();
  }, [refreshServerEntries]);

  // Slot selection and entry now handled directly by inline grid in giveaway box
  // No separate modal needed - slots are selected inline and entry button is on the grid


  // Enhanced date formatting with consistent format
  const formatDate = (dateString) => formatDateTime(dateString);



  // Calendar functionality for date selection
  // const handleCalendarSelect = (date) => {
  //   setSelectedDate(date);
  //   setShowCalendar(false);
  //   const selectedDateStr = formatDate(date);
  //   toast.success(`Showing giveaways for ${selectedDateStr}`);
  // };

  // (removed unused getAccessStatus to fix eslint no-unused-vars)

  // AI CONTEXT: business-logic - 7-day grace period for sold out/ended giveaways
  // Reasoning: Giveaways that sell out or reach end date stay in active tab for 7 days marked as sold out
  // Constraints: After 7 days, they move to past tab. No new entries allowed when sold out or end date reached.
  // Journal: J-20251222-01
  const isExpired = useCallback((g) => {
    if (!g) return false;

    const isSoldOut = (Number(g?.availableSlots) || 0) === 0;
    const hasWinnerOrCompleted = g?.status === 'completed' || !!g?.winner;
    const endDate = g?.endDate ? ensureDate(g.endDate) : null;

    // If there is no end date:
    // - Completed/winner giveaways are immediately past
    // - Sold out giveaways without an end date stay active until manually archived
    if (!endDate) {
      if (hasWinnerOrCompleted) return true;
      return false;
    }

    const endDateReached = endDate && endDate < currentTime;

    // If neither sold out, completed/winner, nor past end date, it's still active
    if (!isSoldOut && !hasWinnerOrCompleted && !endDateReached) return false;

    // For sold out or completed/winner or endDateReached giveaways,
    // keep them in the active tab for 7 days after the end date, then move to past.
    const sevenDaysAfterEnd = new Date(endDate);
    sevenDaysAfterEnd.setDate(sevenDaysAfterEnd.getDate() + 7);

    return currentTime >= sevenDaysAfterEnd;
  }, [currentTime]);

  useEffect(() => {
    if (!shouldAutoEnterPending) return;
    if (!isAuthenticated || !user || giveaways.length === 0) return;
    if (pendingEntryProcessingRef.current) return;
    const pendingEntry = readPendingGiveawayEntry();
    if (!pendingEntry) return;

    const currentUserId = user?.id || user?.email;
    const matchesUser = !pendingEntry.userId || pendingEntry.userId === currentUserId || pendingEntry.userId === user?.email;
    if (!matchesUser) return;

    const giveaway = giveaways.find(g => String(g.id) === String(pendingEntry.giveawayId));
    if (!giveaway) {
      clearPendingGiveawayEntry();
      return;
    }

    if (isExpired(giveaway)) {
      clearPendingGiveawayEntry();
      return;
    }

    const tierName = pendingEntry.tier || giveaway.tier || 'Bronze';
    const creditKey = `${String(tierName).toLowerCase()}Credits`;
    const uniqueSlots = Array.from(new Set(pendingEntry.slots));
    const userEntriesForGiveaway = (giveaway.entries || []).filter(entry =>
      entry.userId === currentUserId || entry.userId === user?.email || entry.userId === user?.id
    );
    const defaultTierLimit = (tierName === 'Gold' || tierName === 'Platinum') ? 10 : 5;
    const maxPerEntry = giveaway.entryLimit ?? defaultTierLimit;
    const remainingSlots = Math.max(0, maxPerEntry - userEntriesForGiveaway.length);

    if (remainingSlots <= 0 || uniqueSlots.length > remainingSlots) {
      clearPendingGiveawayEntry();
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 5;
    const retryDelayMs = 2500;
    pendingEntryProcessingRef.current = true;

    const attemptAutoEntry = async () => {
      if (cancelled) return;
      attempts += 1;
      if (typeof refreshUserCredits === 'function') {
        try {
          await refreshUserCredits();
        } catch (_err) {
          // Non-fatal; credits may still be refreshed via other flows
        }
      }

      const credits = getUserCredits(currentUserId);
      const availableCredits = credits[creditKey] || 0;
      if (availableCredits >= uniqueSlots.length) {
        const success = await enterGiveaway(pendingEntry.giveawayId, uniqueSlots, currentUserId);
        if (success) {
          clearPendingGiveawayEntry();
          const title = pendingEntry.giveawayTitle || giveaway.title || 'giveaway';
          toast.success(`Entered ${uniqueSlots.length} slot${uniqueSlots.length > 1 ? 's' : ''} in ${title}`);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('autoEnter');
            return next;
          }, { replace: true });
        }
        pendingEntryProcessingRef.current = false;
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(attemptAutoEntry, retryDelayMs);
        return;
      }

      pendingEntryProcessingRef.current = false;
      toast('We are still waiting for credits to post from your purchase. Please refresh this page in a moment.');
    };

    attemptAutoEntry();

    return () => {
      cancelled = true;
    };
  }, [shouldAutoEnterPending, isAuthenticated, user, giveaways, enterGiveaway, getUserCredits, refreshUserCredits, isExpired, setSearchParams]);

  useEffect(() => {
    setPendingEntryBanner(readPendingGiveawayEntry());
  }, [shouldAutoEnterPending, modalGiveaway, insufficientCreditsInfo]);

  // Helper function to check if a giveaway is truly active (not sold out, not ended, no winner)
  const isTrulyActive = (g) => {
    if (!g) return false;
    if (isExpired(g)) return false; // Expired giveaways are not active
    const isSoldOut = (Number(g?.availableSlots) || 0) === 0;
    const hasWinnerOrCompleted = g?.status === 'completed' || !!g?.winner;
    const endDate = g?.endDate ? ensureDate(g.endDate) : null;
    const endDateReached = endDate && endDate < currentTime;
    
    // A giveaway is truly active if it's not sold out, not ended, and has no winner
    return !isSoldOut && !hasWinnerOrCompleted && !endDateReached;
  };
  
  const activeGiveaways = giveaways.filter(g => !isExpired(g));
  const pastGiveaways = giveaways.filter(isExpired);
  // Filter giveaways based on selected tab: 'all' shows both, 'active' shows only truly active (excluding sold out/ended), 'past' shows only past
  const filteredByTab = listTab === 'all' 
    ? [...activeGiveaways, ...pastGiveaways]
    : listTab === 'active' 
      ? activeGiveaways.filter(isTrulyActive) // Only show truly active giveaways (exclude sold out/ended)
      : pastGiveaways;
  const isClosedOrSoldOutForList = (g) => {
    if (!g) return true;
    const endDate = g?.endDate ? ensureDate(g.endDate) : null;
    const endDateReached = endDate && endDate < currentTime;
    const hasWinnerOrCompleted = g?.status === 'completed' || !!g?.winner;
    return (g.availableSlots === 0 || g.availableSlots === '0') || endDateReached || hasWinnerOrCompleted;
  };
  const displayedGiveaways = filteredByTab
    .sort((a, b) => {
      // Keep green ACTIVE badge cards first, matching card badge logic.
      const aClosed = isClosedOrSoldOutForList(a);
      const bClosed = isClosedOrSoldOutForList(b);
      if (aClosed !== bClosed) {
        return aClosed ? 1 : -1;
      }

      // Then: current/extended giveaway first (later endDate, newer startDate)
      return compareGiveawaysByRecency(a, b);
    });

  // Compute selectedForWheel after displayedGiveaways is available
  const selectedForWheel = Array.isArray(displayedGiveaways)
    ? displayedGiveaways.find(g => g.id === selectedGiveawayId) || displayedGiveaways[0] || null
    : null;

  useEffect(() => {
    if (!selectedGiveawayId && selectedForWheel) {
      setSelectedGiveawayId(selectedForWheel.id);
    }
  }, [selectedForWheel, selectedGiveawayId]);

  // Broadcast currently selected giveaway id for components that subscribe (e.g., RightSidebar leaderboard)
  useEffect(() => {
    if (selectedForWheel && selectedForWheel.id) {
      try {
        window.dispatchEvent(new CustomEvent('giveaway-selected', { detail: { giveawayId: selectedForWheel.id } }));
      } catch (_e) {}
    }
  }, [selectedForWheel]);

  useEffect(() => {
    setSEO({
      title: 'Giveaways — Tolbert Tactical',
      description: 'Enter exclusive tactical gear giveaways. Subscribers get early access and monthly credits.',
      path: '/giveaways',
      image: '/files/Logo.png'
    });
  }, []);

  // SlotSelectionModal removed - using inline grid only now

  // Function to open the giveaway entry modal
  const openGiveawayModal = useCallback(async (giveawayToOpen) => {
    setSelectedSlots([]); // Reset selections when opening modal
    setModalGiveaway(giveawayToOpen);
    modalGiveawayIdRef.current = giveawayToOpen?.id != null ? String(giveawayToOpen.id) : null;

    // Drop stale insufficient-credits state from a different giveaway
    setInsufficientCreditsInfo((prev) => {
      if (!prev?.giveaway?.id) return prev;
      if (String(prev.giveaway.id) === String(giveawayToOpen.id)) return prev;
      return null;
    });

    const fetchGen = ++modalSlotsFetchGenRef.current;
    
    // Pre-fetch slots data so reserved slots show immediately (not gray)
    if (isApiConfigured()) {
      try {
        const resp = await authorizedFetch(`/giveaways/${giveawayToOpen.id}/slots`, { method: 'GET' });
        if (fetchGen !== modalSlotsFetchGenRef.current) return;
        const taken = Array.isArray(resp) ? resp : (Array.isArray(resp?.taken) ? resp.taken : []);
        setModalSlotsData(taken);
      } catch (_e) {
        if (fetchGen === modalSlotsFetchGenRef.current) {
          setModalSlotsData([]);
        }
      }
    } else {
      setModalSlotsData([]);
    }
  }, []);

  // AI CONTEXT: ux - Giveaway Entry Modal
  // Reasoning: Users want a focused modal with just the slot grid when clicking giveaway cards
  // Constraints: Modal should show larger slot grid, cancel button, and enter button only
  // Journal: J-20251226-04
  // AI CONTEXT: bug-fix - Move refs outside useMemo (hooks can't be called in callbacks)
  // Journal: J-20260112-06
  const modalContainerRef = React.useRef(null);
  
  // AI CONTEXT: bug-fix - Memoize modal to prevent re-creation on every parent render
  // Reasoning: Parent re-renders every second (countdown timer), causing modal reset
  // Journal: J-20260112-06
  const GiveawayEntryModal = React.useMemo(() => {
    if (!modalGiveaway) return null;

    const past = isExpired(modalGiveaway);
    const endDate = modalGiveaway?.endDate ? ensureDate(modalGiveaway.endDate) : null;
    const endDateReached = endDate && endDate < currentTime;
    const hasWinnerOrCompleted = modalGiveaway?.status === 'completed' || !!modalGiveaway?.winner;
    // A giveaway is sold out/closed if: slots are 0, end date reached, or has winner/completed
    // This applies to both past and non-past giveaways
    const isClosedOrSoldOut = (modalGiveaway.availableSlots === 0 || modalGiveaway.availableSlots === '0') || endDateReached || hasWinnerOrCompleted;
    // For non-past giveaways: also check if sold out (for display logic)
    const isSoldOut = !past && ((modalGiveaway.availableSlots === 0 || modalGiveaway.availableSlots === '0') || endDateReached);
    // AI CONTEXT: business-logic - Disable entries for ended/sold-out giveaways
    // Reasoning: All slot reservations should show but no entries should be allowed
    // Journal: J-20251226-05
    const isEndedOrSoldOut = past || isSoldOut || hasWinnerOrCompleted;
    const canEnter = !isEndedOrSoldOut && canViewGiveaway(modalGiveaway, user);
    // Always show winning slot in gold for all sold-out/ended giveaways
    const winningSlot = modalGiveaway?.winner?.slot || null;

    const handleCancel = () => {
      setModalGiveaway(null);
      modalGiveawayIdRef.current = null;
      setSelectedSlots([]);
      setModalSlotsData(null); // Clear pre-fetched data when closing modal
      setInsufficientCreditsInfo(null); // Prevent stale checkout targeting wrong giveaway
    };

    const handleBackdropClick = (e) => {
      // Close modal when clicking backdrop
      if (e.target === e.currentTarget) {
        handleCancel();
      }
    };

    const handleEnter = async () => {
      if (selectedSlots.length === 0) {
        toast.error('Please select at least one slot');
        return;
      }

      if (!user) {
        toast.error('Please log in to enter giveaways');
        return;
      }

      // Check credits before entry
      const credits = getUserCredits(user.id);
      const creditKey = `${String(modalGiveaway?.tier || 'Bronze').toLowerCase()}Credits`;
      const availableCredits = credits[creditKey] || 0;
      const requiredCredits = selectedSlots.length;
      const tierName = String(modalGiveaway?.tier || 'Bronze');

      if (availableCredits < requiredCredits) {
        // Show insufficient credits modal
        const missingCredits = requiredCredits - availableCredits;
        setInsufficientCreditsInfo({
          availableCredits,
          selectedSlotsCount: requiredCredits,
          missingCredits,
          tier: tierName,
            giveaway: modalGiveaway,
            selectedSlots: selectedSlots
        });
        return;
      }

      // User has sufficient credits, proceed with entry (use pinned ID to avoid stale closure)
      const giveawayId = modalGiveawayIdRef.current || modalGiveaway?.id;
      if (!giveawayId) {
        toast.error('Giveaway not found. Please close and reopen the entry modal.');
        return;
      }
      const success = await enterGiveaway(giveawayId, selectedSlots, user.id);
      
      if (success) {
        // Refresh entries to show newly entered slots highlighted in green
        if (isApiConfigured()) {
          await refreshServerEntries();
        }
        toast.success(`Successfully entered ${selectedSlots.length} slot${selectedSlots.length > 1 ? 's' : ''} in ${modalGiveaway.title}`);
        // Manual entry supersedes any stale pending checkout entry
        clearPendingGiveawayEntry();
        setPendingEntryBanner(null);
        handleCancel(); // Close modal after successful entry
      }
    };

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={handleBackdropClick}
      >
        <div
          ref={modalContainerRef}
          className="glass-card-dark rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'none' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 flex-1 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-theme-silver">{modalGiveaway.title}</h3>
              <button
                onClick={handleCancel}
                className="text-theme-silver-light hover:text-theme-silver text-2xl"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            {/* AI CONTEXT: ux - Scrollable slot grid for large giveaways
                Reasoning: Support 100+ slot grids with nested scrolling
                Journal: J-20260108-06 */}
            <div className="mb-6 flex-1 min-h-0">
              <GiveawaySlotGrid
                giveaway={modalGiveaway}
                user={user}
                serverEntries={serverEntries}
                variant="modal"
                maxHeight="calc(90vh - 200px)"
                showEntryButton={false}
                readOnly={!canEnter} // Read-only mode for ended/sold-out giveaways
                winningSlot={winningSlot} // Highlight winning slot in gold for all sold-out/ended giveaways
                initialTakenSlots={modalSlotsData} // Pre-fetched slots data for immediate display
                isSoldOut={isClosedOrSoldOut} // Pass sold-out status for legend display (includes past giveaways)
                hideWinnerUntil={modalGiveaway?.hideWinnerUntil} // AI CONTEXT: Hide winner until timestamp (J-20260112-01)
                selectedSlots={selectedSlots} // Controlled selected slots from parent state
                onSelectionChange={(slots) => {
                  // AI CONTEXT: bug-fix - Simple state update, browser preserves grid scroll naturally
                  // Reasoning: Modal doesn't scroll (overflow-hidden), grid scroll preserved by browser
                  // Journal: J-20260112-06
                  setSelectedSlots(slots);
                }}
              />
            </div>

            {/* AI CONTEXT: ux - Sticky action buttons for large grids
                Reasoning: Keep buttons visible when scrolling through 100+ slots
                Journal: J-20260108-06 */}
            {canEnter ? (
              <div className="flex gap-3 sticky bottom-0 bg-gradient-to-t from-[rgba(15,16,18,0.98)] to-[rgba(15,16,18,0.95)] pt-4 -mx-6 px-6 -mb-6 pb-6">
                <button
                  onClick={handleCancel}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEnter}
                  disabled={selectedSlots.length === 0}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Enter Giveaway ({selectedSlots.length} selected)
                </button>
              </div>
            ) : (
              <div className="flex gap-3 sticky bottom-0 bg-gradient-to-t from-[rgba(15,16,18,0.98)] to-[rgba(15,16,18,0.95)] pt-4 -mx-6 px-6 -mb-6 pb-6">
                <button
                  onClick={handleCancel}
                  className="btn-secondary flex-1"
                >
                  Close
                </button>
                {!isAuthenticated && !isEndedOrSoldOut && (
                  <Link
                    to="/login"
                    className="btn-primary flex-1 text-center"
                    onClick={handleCancel}
                  >
                    Login to Enter
                  </Link>
                )}
                {isEndedOrSoldOut && (
                  <div className="flex-1 text-center text-theme-silver-light text-sm py-2 flex items-center justify-center">
                    {past ? 'This giveaway has ended' : isSoldOut ? 'This giveaway is sold out' : 'Winner has been selected'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [modalGiveaway, selectedSlots, user, isAuthenticated, serverEntries, modalSlotsData, currentTime, enterGiveaway, getUserCredits, refreshServerEntries, canViewGiveaway, isExpired, setModalGiveaway, setSelectedSlots, setModalSlotsData, setInsufficientCreditsInfo]);

  // InsufficientCreditsModal - Shows credits vs slots with checkout button
  // AI CONTEXT: business-logic - Modal for purchasing credits when insufficient credits for giveaway entry
  // Journal: J-20251219-01
  const InsufficientCreditsModal = () => {
    if (!insufficientCreditsInfo) return null;

    const { availableCredits, selectedSlotsCount, missingCredits, tier, giveaway: pendingGiveaway } = insufficientCreditsInfo;

    const handleCheckout = () => {
      // Find products matching the giveaway tier
      const tierProducts = (products || [])
        .filter(p => {
          const productTier = String(p?.tier || 'Bronze').toLowerCase();
          const giveawayTier = String(tier || 'Bronze').toLowerCase();
          return productTier === giveawayTier && (p?.entries > 0 || p?.credits > 0);
        })
        .sort((a, b) => {
          // Sort by price (lowest first) to show affordable options first
          const priceA = a.salesPrice && a.salesPrice > 0 ? a.salesPrice : a.price;
          const priceB = b.salesPrice && b.salesPrice > 0 ? b.salesPrice : b.price;
          return priceA - priceB;
        });

      if (tierProducts.length === 0) {
        toast.error(`No ${tier} products available. Please visit the Store to purchase credits.`);
        setInsufficientCreditsInfo(null);
        return;
      }

      // Use the first product (cheapest) and calculate the quantity needed
      const productToAdd = tierProducts[0];
      const creditsPerProduct = productToAdd.credits || productToAdd.entries || 1;
      // Calculate quantity needed: round up to ensure we get at least the required credits
      const quantityNeeded = Math.ceil(missingCredits / creditsPerProduct);
      
      const product = {
        id: productToAdd.id,
        name: productToAdd.name || productToAdd.title,
        price: productToAdd.price,
        salesPrice: productToAdd.salesPrice,
        entries: creditsPerProduct,
        tier: productToAdd.tier || tier
      };

      addToCart(product, quantityNeeded);
      const totalCreditsAdded = quantityNeeded * creditsPerProduct;
      toast.success(`Added ${quantityNeeded} ${tier} product${quantityNeeded > 1 ? 's' : ''} (${totalCreditsAdded} credit${totalCreditsAdded > 1 ? 's' : ''}) to cart`);

      if (insufficientCreditsInfo?.giveaway?.id && Array.isArray(insufficientCreditsInfo?.selectedSlots) && insufficientCreditsInfo.selectedSlots.length > 0) {
        writePendingGiveawayEntry({
          giveawayId: String(insufficientCreditsInfo.giveaway.id),
          giveawayTitle: insufficientCreditsInfo.giveaway.title,
          slots: insufficientCreditsInfo.selectedSlots,
          tier,
          userId: user?.id || user?.email,
        });
      }

      // Close modal and navigate to checkout
      setInsufficientCreditsInfo(null);
      navigate('/checkout');
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div
          className="glass-card-dark w-full max-w-md rounded-xl overflow-hidden"
          style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
        >
          <div className="p-6">
            <h3 className="text-xl font-bold text-theme-silver mb-4">
              Insufficient Credits
            </h3>
            
            <div className="mb-6">
              {pendingGiveaway?.title && (
                <p className="text-theme-silver mb-3">
                  Giveaway: <span className="font-semibold text-custom-orange">{pendingGiveaway.title}</span>
                </p>
              )}
              <p className="text-theme-silver-light mb-4">
                You have <span className="font-semibold text-custom-orange">{availableCredits}</span> {tier} credit{availableCredits !== 1 ? 's' : ''} and selected <span className="font-semibold text-custom-orange">{selectedSlotsCount}</span> slot{selectedSlotsCount !== 1 ? 's' : ''}, but need <span className="font-semibold text-custom-orange">{missingCredits}</span> more credit{missingCredits > 1 ? 's' : ''}.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setInsufficientCreditsInfo(null);
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleCheckout}
                className="btn-primary flex-1"
              >
                Checkout
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // SlotSwapModal removed

  // Public visibility enabled: unauthenticated users can view giveaways; entering still requires auth
  return (
    <div className="min-h-screen bg-theme-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!shouldAutoEnterPending && pendingEntryBanner && (
          <div className="mb-6 glass-card-dark rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-theme-silver text-sm">
              You have a pending entry
              {pendingEntryBanner.giveawayTitle ? (
                <> for <span className="font-semibold text-custom-orange">{pendingEntryBanner.giveawayTitle}</span></>
              ) : null}
              {' '}({pendingEntryBanner.slots.length} slot{pendingEntryBanner.slots.length > 1 ? 's' : ''} selected).
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSearchParams({ autoEnter: '1' }, { replace: true })}
                className="btn-primary text-sm py-2 px-4"
              >
                Finish Entry
              </button>
              <button
                type="button"
                onClick={() => {
                  clearPendingGiveawayEntry();
                  setPendingEntryBanner(null);
                  toast('Pending giveaway entry cleared');
                }}
                className="btn-secondary text-sm py-2 px-4"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {/* Header with Calendar and Clock Features */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-theme-silver mb-2">
                {listTab === 'active' ? 'Active Giveaways' : listTab === 'past' ? 'Past Giveaways' : 'Giveaways'}
              </h1>
              <p className="text-lg text-theme-silver-light">
                Enter exclusive giveaways for amazing tactical gear and firearms
              </p>
            </div>
            
            {/* Top-right tools hidden per request (clock and date filter) */}
            <div className="hidden"></div>

          {/* Tabs: Active | Past */}
          <div className="flex gap-2">
            {[
              { id: 'active', label: 'Active Giveaways' },
              { id: 'past', label: 'Past Giveaways' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => {
                  // Toggle: if clicking the same button, show all; otherwise filter to that type
                  setListTab(listTab === t.id ? 'all' : t.id);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  listTab === t.id
                    ? 'bg-custom-orange text-white'
                    : 'glass-card-dark text-theme-silver hover:bg-glass-white/10 border border-theme-silver/20'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          </div>

          {/* Calendar Modal hidden per request */}
          {false && false && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="glass-card-dark rounded-xl p-6 max-w-sm w-full">Hidden</div>
            </div>
          )}
        </div>

        {/* User Credits Display */}
        {isAuthenticated && user && (
          <div className="glass-card-dark mb-3 p-2 md:p-3 shadow-glow border border-theme-silver/20">
            <div className="flex items-center justify-between mb-1.5 md:mb-2">
              <div className="flex items-center space-x-1.5">
                <CreditCard className="h-3.5 w-3.5 md:h-4 md:w-4 text-custom-orange" />
                <h2 className="text-sm md:text-base font-semibold text-theme-silver">Your Credits</h2>
              </div>
            </div>
            
            <div className="grid grid-cols-4 gap-1.5 md:gap-2">
              {['Bronze', 'Silver', 'Gold', 'Platinum'].map(tier => {
                const credits = getUserCredits(user.id);
                const creditKey = `${tier.toLowerCase()}Credits`;
                const creditCount = credits[creditKey] || 0;

                return (
                  <div key={tier} className="text-center glass-card-dark p-1.5 md:p-2 rounded border border-theme-silver/10">
                    <div className="mb-0.5 md:mb-1">
                      <span className={`tier-badge tier-${tier.toLowerCase()}`}>{tier}</span>
                    </div>
                    <p className="text-lg md:text-xl font-bold text-theme-silver leading-tight">{creditCount}</p>
                    <p className="text-[10px] md:text-xs text-theme-silver-light leading-tight">credits</p>
                  </div>
                );
              })}
            </div>

          </div>
        )}

        {/* Premium Benefits Row - Hidden for paid subscribers and admins */}
        {!isSubscriber && !isAdmin && (
          <div className="mb-6">
            <div className="glass-card-dark p-4 rounded-xl border border-custom-orange/20 bg-gradient-to-r from-custom-orange/5 to-transparent">
              <h3 className="text-lg font-bold text-theme-silver mb-3 flex items-center">
                <Crown className="h-5 w-5 text-custom-orange mr-2" />
                Premium Benefits
              </h3>
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-custom-orange rounded-full"></div>
                  <span className="text-theme-silver-light text-sm">48-hour early access</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-custom-orange rounded-full"></div>
                  <span className="text-theme-silver-light text-sm">1 Bronze credit monthly</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-custom-orange rounded-full"></div>
                  <span className="text-theme-silver-light text-sm">Priority support</span>
                </div>
                <div className="ml-auto">
                  {!isAuthenticated ? (
                    <button
                      onClick={() => {
                        const product = { id: 'subscription-bronze', name: 'Bronze Subscription', price: 24.99, quantity: 1, entries: 1, tier: 'Bronze', isSubscription: true, interval: 'month' };
                        addToCart(product, 1);
                        window.location.href = '/checkout';
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500/50 text-sm"
                    >
                      Start Bronze Subscription
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        const product = { id: 'subscription-bronze', name: 'Bronze Subscription', price: 24.99, quantity: 1, entries: 1, tier: 'Bronze', isSubscription: true, interval: 'month' };
                        addToCart(product, 1);
                        window.location.href = '/checkout';
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500/50 text-sm"
                    >
                      Upgrade to Premium
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Two Column Layout: Leaderboard + Product Carousel */}
        <div className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Top Customers and Top Entries Combined Box */}
          {/* AI CONTEXT: leaderboards - Split single box into two sections: Top Customers (left) and Top Entries (right)
              Reasoning: Client wants both leaderboards in same box without increasing height
              Constraints: Keep same box, no height increase, same styling
              Journal: J-20260112-02 */}
          <div className="glass-card-dark p-4 rounded-xl border border-theme-silver/20">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 divide-y sm:divide-y-0 sm:divide-x divide-theme-silver/10">
              {/* Top Customers Section */}
              <div className="pb-4 sm:pb-0 sm:pr-4">
                <h3 className="text-base font-bold text-theme-silver mb-3">Top Customers</h3>
                {loadingLeaders ? (
                  <div className="text-xs text-theme-silver-light">Loading…</div>
                ) : leaders.length > 0 ? (
                  <ul className="space-y-2 text-sm">
                    {leaders.slice(0, 3).map((customer, idx) => (
                      <li key={`${customer.displayName}-${idx}`} className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0 flex-1">
                          <div className="w-5 h-5 rounded-full bg-gradient-to-r from-custom-orange to-yellow-500 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                            {idx + 1}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium text-theme-silver truncate">{customer.displayName}</span>
                          </div>
                        </div>
                        <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-custom-orange/20 to-yellow-500/20 border border-custom-orange/30 text-custom-orange font-bold text-xs flex-shrink-0">
                          {customer.totalCredits || customer.purchaseCount}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-4">
                    <div className="text-theme-silver-light text-xs">No customers yet</div>
                  </div>
                )}
              </div>

              {/* Top Entries Section */}
              <div className="pt-4 sm:pt-0 sm:pl-4">
                <h3 className="text-base font-bold text-theme-silver mb-3">Top Entries</h3>
                {loadingTopEntries ? (
                  <div className="text-xs text-theme-silver-light">Loading…</div>
                ) : topEntries.length > 0 ? (
                  <ul className="space-y-2 text-sm">
                    {topEntries.slice(0, 3).map((entrant, idx) => (
                      <li key={`${entrant.displayName}-${idx}`} className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0 flex-1">
                          <div className="w-5 h-5 rounded-full bg-gradient-to-r from-custom-orange to-yellow-500 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                            {idx + 1}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="font-medium text-theme-silver truncate">{entrant.displayName}</span>
                          </div>
                        </div>
                        <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-custom-orange/20 to-yellow-500/20 border border-custom-orange/30 text-custom-orange font-bold text-xs flex-shrink-0">
                          {entrant.totalEntries}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-4">
                    <div className="text-theme-silver-light text-xs">No entries yet</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Boost Your Entries Product Carousel */}
          <div className="glass-card-dark p-4 rounded-xl border border-theme-silver/20">
            <h3 className="text-lg font-bold text-theme-silver mb-3">Boost your entries</h3>
            <div className="flex gap-3 overflow-x-auto pb-2" style={{ WebkitOverflowScrolling: 'touch' }}>
              {carouselProducts.map(p => (
                <button
                  key={p.id}
                  onClick={(e) => {
                    // AI CONTEXT: ux - Prevent slot grid modal from opening when clicking boost buttons
                    // Reasoning: Boost buttons should only add to cart, not open slot grid modal
                    // Journal: J-20251226-11
                    e.stopPropagation(); // Prevent modal from opening
                    // AI CONTEXT: payments - Preserve subscription flags when adding to cart
                    // Journal: J-20260112-01
                    const product = { 
                      id: p.id, 
                      name: p.name || p.title, 
                      price: p.price, 
                      quantity: 1, 
                      entries: p.credits || p.entries, 
                      tier: p.tier || 'Bronze',
                      isSubscription: p.isSubscription || false,
                      interval: p.interval || 'month'
                    };
                    addToCart(product, 1);
                    try { window.dispatchEvent(new Event('purchase-success')); } catch (_) {}
                    setPrompt({ product: p });
                  }}
                  className="relative w-40 flex-shrink-0 text-left rounded-lg border border-theme-silver/20 overflow-hidden group"
                  style={{ background: 'linear-gradient(180deg, rgba(30,32,35,0.8), rgba(15,16,18,0.8))' }}
                >
                  <div className="relative">
                    <S3Image s3KeyOrUrl={p.image} alt={p.name || p.title} className="w-40 h-24 object-cover" fallback="/files/Logo.png" />
                    {/* metallic micro-shine */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
                      background: 'linear-gradient(75deg, rgba(255,255,255,0.0) 20%, rgba(255,255,255,0.25) 40%, rgba(255,255,255,0.0) 60%)',
                      transform: 'translateX(-30%)',
                      animation: 'shine 1.2s ease-out both'
                    }} />
                    <style>{`@keyframes shine { to { transform: translateX(30%); } }`}</style>
                  </div>
                  <div className="p-3">
                    <div className="text-xs text-theme-silver-light truncate">{p.name || p.title}</div>
                    <div className="flex items-center justify-between mt-1">
                      {p.salesPrice ? (
                        <div className="flex items-center space-x-1">
                          <span className="text-[10px] text-custom-orange line-through">${Number(p.price).toFixed(2)}</span>
                          <span className="text-sm font-semibold text-theme-silver">${Number(p.salesPrice).toFixed(2)}</span>
                        </div>
                      ) : (
                        <span className="text-sm font-semibold text-theme-silver">${Number(p.price).toFixed(2)}</span>
                      )}
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500 text-white">+{p.credits || p.entries} credits</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Post-add prompt modal */}
        {prompt && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="glass-card-dark w-full max-w-sm rounded-xl border border-theme-silver/20 p-6">
              <h3 className="text-lg font-semibold text-theme-silver mb-2">Item added to cart</h3>
              <p className="text-sm text-theme-silver-light mb-4">What would you like to do next?</p>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setPrompt(null)} className="px-4 py-2 rounded-lg glass-card-dark border border-theme-silver/20 text-theme-silver text-sm">Add More</button>
                <a href={`/store?tier=Bronze`} className="px-4 py-2 rounded-lg glass-card-dark border border-theme-silver/20 text-theme-silver text-sm">Go to Store</a>
                <a href="/checkout" className="px-4 py-2 rounded-lg bg-custom-orange text-white text-sm">Checkout</a>
              </div>
            </div>
          </div>
        )}

        {/* No Credits Modal - Hidden when wheel is disabled */}
        {SHOW_WHEEL && noCreditsInfo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog" aria-modal="true">
            <div className="glass-card-dark w-full max-w-sm rounded-xl border border-theme-silver/20 p-6">
              <h3 className="text-lg font-semibold text-theme-silver mb-2">Insufficient Credits</h3>
              <p className="text-sm text-theme-silver-light mb-4">
                You need <span className="font-semibold text-theme-silver">{noCreditsInfo.tier}</span> credits to reserve slot <span className="font-semibold text-theme-silver">{noCreditsInfo.slot}</span>.
              </p>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setNoCreditsInfo(null)}
                  className="px-4 py-2 rounded-lg glass-card-dark border border-theme-silver/20 text-theme-silver text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setNoCreditsInfo(null); window.location.href = '/store'; }}
                  className="px-4 py-2 rounded-lg bg-custom-orange text-white text-sm"
                >
                  Go to Store
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reserve Confirmation Modal - Hidden when wheel is disabled */}
        {SHOW_WHEEL && reserveInfo && selectedForWheel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog" aria-modal="true">
            <div className="glass-card-dark w-full max-w-sm rounded-xl border border-theme-silver/20 p-6">
              <h3 className="text-lg font-semibold text-theme-silver mb-2">Reserve Slot {reserveInfo.slot}</h3>
              <p className="text-sm text-theme-silver-light mb-4">
                Confirm using one {String(selectedForWheel.tier || 'Bronze')} credit to reserve this slot?
              </p>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setReserveInfo(null)}
                  className="px-4 py-2 rounded-lg glass-card-dark border border-theme-silver/20 text-theme-silver text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      const ok = await enterGiveaway(selectedForWheel.id, [reserveInfo.slot], user.id);
                      if (ok) {
                        toast.success(`Reserved slot ${reserveInfo.slot}`);
                        setReserveInfo(null);
                        // UI pause was used with wheel; no-op since wheel is hidden
                        // Hint the wheel to refresh: rely on its polling; no further action required
                      }
                    } catch (e) {
                      toast.error(String(e?.message || 'Failed to reserve slot'));
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-custom-orange text-white text-sm"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Giveaways Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedGiveaways.map(giveaway => {
            const past = isExpired(giveaway);
            const goBronzeCheckout = () => {
              const product = { id: 'subscription-bronze', name: 'Bronze Subscription', price: 24.99, quantity: 1, entries: 1, tier: 'Bronze', isSubscription: true, interval: 'month' };
              addToCart(product, 1);
              window.location.href = '/checkout';
            };
            const serverCount = Array.isArray(serverEntries)
              ? serverEntries.filter(e => e.giveawayId === giveaway.id).length
              : 0;
            const userEntriesCount = serverCount > 0
              ? serverCount
              : giveaway.entries.filter(entry => entry.userId === user?.id).length;
            // credits are displayed in the modal; eligibility enforced on submit

            const images = Array.isArray(giveaway.images) && giveaway.images.length > 0
              ? giveaway.images
              : [giveaway.image || '/files/Logo.png'];

            const isSelected = selectedGiveawayId === giveaway.id;
            // AI CONTEXT: business-logic - Mark as sold out if slots are 0 OR end date reached (even within 7-day grace period)
            // Reasoning: Giveaways that reach end date or sell out should be marked as sold out and block entries
            // Journal: J-20251222-01
            const endDate = giveaway?.endDate ? ensureDate(giveaway.endDate) : null;
            const endDateReached = endDate && endDate < currentTime;
            const hasWinnerOrCompleted = giveaway?.status === 'completed' || !!giveaway?.winner;
            // A giveaway is sold out/closed if: slots are 0, end date reached, or has winner/completed
            // This applies to both past and non-past giveaways
            const isClosedOrSoldOut = (giveaway.availableSlots === 0 || giveaway.availableSlots === '0') || endDateReached || hasWinnerOrCompleted;
            const isClassified = shouldShowClassifiedPrize(giveaway, {
              isSubscriber,
              isAdmin,
              isPast: past,
              isClosed: isClosedOrSoldOut,
              isAuthenticated,
              now: currentTime,
            });
            const canEnter = !past && !isClassified && canViewGiveaway(giveaway, user);
            // For non-past giveaways: also check if sold out (for badge display logic)
            const isSoldOut = !past && ((giveaway.availableSlots === 0 || giveaway.availableSlots === '0') || endDateReached);
            // Extract winning slot - handle various formats (object with slot property, or direct slot value)
            // The winner can be stored as: { slot: "123" } or { slot: 123 } or just a slot number
            let winningSlot = null;
            if (giveaway?.winner) {
              if (typeof giveaway.winner === 'object' && giveaway.winner !== null) {
                // Winner is an object, extract the slot property
                winningSlot = giveaway.winner.slot || null;
              } else if (typeof giveaway.winner === 'string' || typeof giveaway.winner === 'number') {
                // Winner is directly a slot number (unlikely but handle it)
                winningSlot = giveaway.winner;
              }
            }
            // Normalize to string for consistent comparison (component will convert to number)
            if (winningSlot !== null && winningSlot !== undefined) {
              winningSlot = String(winningSlot);
            } else {
              winningSlot = null;
            }
            return (
              <div
                key={giveaway.id}
                className={`glass-card-dark hover:shadow-glow transition-all duration-300 ${isSelected ? 'ring-2 ring-custom-orange/60' : ''} cursor-pointer`}
                onClick={async (e) => {
                  // AI CONTEXT: ux - Open entry modal when giveaway card is clicked (but not when clicking buttons inside)
                  // Reasoning: Users want focused modal with slot grid when clicking giveaway cards
                  // Journal: J-20251226-04, J-20251226-11
                  // Check if the click target is a button or link (but not the card itself)
                  const target = e.target;
                  const buttonOrLink = target.closest('button, a');
                  
                  // Only prevent modal if we clicked on a button/link that's inside the card (not the card itself)
                  if (buttonOrLink && buttonOrLink !== e.currentTarget && e.currentTarget.contains(buttonOrLink)) {
                    return; // Don't open modal if clicking a button or link inside the card
                  }

                  if (isClassified) {
                    return;
                  }
                  
                  await openGiveawayModal(giveaway);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={async (e) => { 
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    await openGiveawayModal(giveaway);
                  }
                }}
                aria-pressed={isSelected}
              >
                <div className="relative">
                  {isClassified ? (
                    <ClassifiedPrizeMedia onUnlock={goBronzeCheckout}>
                      <CardImageGallery items={images} alt="Classified giveaway" />
                    </ClassifiedPrizeMedia>
                  ) : (
                    <CardImageGallery items={images} alt={giveaway.title} />
                  )}
                  <div className="absolute top-3 right-3">
                    <span className={`tier-badge tier-${(giveaway?.tier || 'Bronze').toLowerCase()}`}>
                      {giveaway.tier}
                    </span>
                  </div>
                  {isClosedOrSoldOut && (
                    <div className="absolute top-3 left-3 bg-red-600/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-bold text-white">
                      SOLD OUT
                    </div>
                  )}
                  {!isClosedOrSoldOut && (
                    <div className="absolute top-3 left-3 bg-green-600/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-bold text-white">
                      ACTIVE
                    </div>
                  )}
                </div>

                <div className={isClassified ? 'px-6 pt-3 pb-6' : 'p-6'}>
                  {!isClassified && (
                    <>
                      <h3 className="text-xl font-bold text-theme-silver mb-2">
                        {giveaway.title}
                      </h3>
                      <p className="text-theme-silver-light mb-4">
                        {giveaway.description}
                      </p>
                    </>
                  )}

                  {/* Countdown Timer - Prominent Display */}
                  {!past && giveaway.endDate && !giveaway.winner && giveaway.status !== 'completed' && (
                    <div className="mb-6">
                      <CountdownTimer 
                        endDate={isSoldOut ? new Date().toISOString() : giveaway.endDate}
                        currentTime={currentTime}
                        variant="default"
                        className="bg-glass-copper/10 border border-custom-orange/30"
                        isActive={isTrulyActive(giveaway)}
                      />
                    </div>
                  )}
                  {/* AI CONTEXT: admin-functionality - Show "Winner Selected" message only if not hidden */}
                  {/* Reasoning: Respect hideWinnerUntil to control winner visibility, no announcement banner when hidden */}
                  {/* Journal: J-20260112-02 */}
                  {(giveaway.winner || giveaway.status === 'completed') && (!giveaway.hideWinnerUntil || Date.now() >= giveaway.hideWinnerUntil) && (
                    <div className="mb-6 p-4 bg-green-600/10 border border-green-600/30 rounded-lg text-center">
                      <p className="text-green-400 font-semibold">
                        🎉 Winner Selected
                        {giveaway.winner?.slot && (
                          <span className="text-yellow-500"> ({giveaway.winner.slot})</span>
                        )}
                        !
                      </p>
                      <p className="text-sm text-theme-silver-light mt-1">Now closed. Click to View Winner</p>
                    </div>
                  )}

                  {/* Sold Out Message with Read-Only Slot Grid */}
                  {isClosedOrSoldOut && (
                    <>
                      <div className="mb-4 p-4 bg-custom-orange/10 border border-custom-orange/30 rounded-lg text-center">
                        <Link
                          to={isClassified ? '/acquisition' : `/acquisition?item=${encodeURIComponent(giveaway.title || 'firearm')}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-custom-orange hover:text-custom-orange/80 font-semibold underline inline-block transition-colors"
                        >
                          Request to Purchase
                        </Link>
                      </div>
                      
                      {/* AI CONTEXT: ux - Show read-only slot grid for sold out giveaways (past or active)
                          Reasoning: Users should be able to see their entries highlighted in green even when giveaway is sold out
                          Constraints: Show for all users (authenticated see their entries in green), read-only (no entry button)
                          Journal: J-20251226-03 */}
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-theme-silver mb-2">
                          All Entries <span className="text-xs text-theme-silver-light">(read-only)</span>
                        </h4>
                        <GiveawaySlotGrid
                          giveaway={giveaway}
                          user={user} // Can be null for unauthenticated users
                          serverEntries={isAuthenticated && user ? serverEntries : []} // Only pass entries for authenticated users
                          variant="inline"
                          maxHeight="200px"
                          showEntryButton={false}
                          readOnly={true} // All slots disabled for sold-out giveaways
                          winningSlot={winningSlot} // Highlight winning slot in gold for all sold-out/ended giveaways
                          isSoldOut={isClosedOrSoldOut} // Show "winner" in legend for sold-out/ended giveaways
                          hideWinnerUntil={giveaway?.hideWinnerUntil} // AI CONTEXT: Hide winner until timestamp (J-20260112-01)
                        />
                      </div>
                    </>
                  )}
                  {/* Guest / locked: show read-only number grid so reserved slots are visible */}
                  {!past && !canEnter && !isClosedOrSoldOut && !giveaway.winner && giveaway.status !== 'completed' && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-theme-silver mb-2">
                        {t('giveaways.availableSlots')}{' '}
                        <span className="text-xs text-theme-silver-light">(numbers shown — red = reserved)</span>
                      </h4>
                      <GiveawaySlotGrid
                        giveaway={giveaway}
                        user={user}
                        serverEntries={[]}
                        variant="inline"
                        maxHeight="200px"
                        showEntryButton={false}
                        readOnly={true}
                        winningSlot={null}
                        isSoldOut={false}
                        hideWinnerUntil={giveaway?.hideWinnerUntil}
                      />
                      {!isAuthenticated && !isClassified && (
                        <div className="flex items-center gap-3 mt-3">
                          <Link to="/register" className="btn-primary flex-1 text-center">
                            Register to enter
                          </Link>
                          <Link to="/login" className="btn-secondary flex-1 text-center">
                            Login to enter
                          </Link>
                        </div>
                      )}
                      {isClassified && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            goBronzeCheckout();
                          }}
                          className="btn-primary w-full text-center mt-3 text-lg sm:text-xl py-4 px-6 font-semibold"
                        >
                          Subscribe to Unlock
                        </button>
                      )}
                    </div>
                  )}

                  {/* Tier-matched Product Carousel inside giveaway card - Only show for active (not closed/sold out) giveaways */}
                  {!isClosedOrSoldOut && !isClassified && (() => {
                    const giveawayTier = (giveaway?.tier || 'Bronze').toLowerCase();
                    const tierProducts = (products || [])
                      .filter(p => (p?.credits > 0 || p?.entries > 0) && ((p?.tier || 'Bronze').toLowerCase() === giveawayTier))
                      .slice(0, 4);
                    if (!tierProducts || tierProducts.length === 0) return null;
                    return (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-theme-silver mb-2">Boost your entries</h4>
                        <div className="flex gap-3 overflow-x-auto pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                          {tierProducts.map(p => (
                            <button
                              key={p.id}
                              onClick={(e) => {
                                // AI CONTEXT: ux - Prevent slot grid modal from opening when clicking boost buttons
                                // Reasoning: Boost buttons should only add to cart, not open slot grid modal
                                // Journal: J-20251226-11
                                e.stopPropagation(); // Prevent modal from opening
                                // AI CONTEXT: payments - Preserve subscription flags when adding to cart
                                // Journal: J-20260112-01
                                const product = { 
                                  id: p.id, 
                                  name: p.name || p.title, 
                                  price: p.price, 
                                  salesPrice: p.salesPrice, 
                                  quantity: 1, 
                                  entries: p.credits || p.entries, 
                                  tier: p.tier || 'Bronze',
                                  isSubscription: p.isSubscription || false,
                                  interval: p.interval || 'month'
                                };
                                addToCart(product, 1);
                                try { window.dispatchEvent(new Event('purchase-success')); } catch (_) {}
                                setPrompt({ product: p });
                              }}
                              className="relative w-36 flex-shrink-0 text-left rounded-lg border border-theme-silver/20 overflow-hidden group"
                              style={{ background: 'linear-gradient(180deg, rgba(30,32,35,0.8), rgba(15,16,18,0.8))' }}
                            >
                              <div className="relative">
                                <S3Image s3KeyOrUrl={p.image} alt={p.name || p.title} className="w-36 h-20 object-cover" fallback="/files/Logo.png" />
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
                                  background: 'linear-gradient(75deg, rgba(255,255,255,0.0) 20%, rgba(255,255,255,0.25) 40%, rgba(255,255,255,0.0) 60%)',
                                  transform: 'translateX(-30%)',
                                  animation: 'shine 1.2s ease-out both'
                                }} />
                                <style>{`@keyframes shine { to { transform: translateX(30%); } }`}</style>
                              </div>
                              <div className="p-2">
                                <div className="text-[11px] text-theme-silver-light truncate">{p.name || p.title}</div>
                                <div className="flex items-center justify-between mt-1">
                                  {p.salesPrice ? (
                                    <div className="flex items-center space-x-1">
                                      <span className="text-[10px] text-custom-orange line-through">${Number(p.price).toFixed(2)}</span>
                                      <span className="text-xs font-semibold text-theme-silver">${Number(p.salesPrice).toFixed(2)}</span>
                                    </div>
                                  ) : (
                                    <span className="text-xs font-semibold text-theme-silver">${Number(p.price).toFixed(2)}</span>
                                  )}
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500 text-white">+{p.credits || p.entries}</span>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="space-y-3 mb-6">
                    {/* AI CONTEXT: business-logic - Hide remaining slot counts once giveaway has ended */}
                    {/* Reasoning: When end date is reached we treat the giveaway as sold out; don't show remaining slots */}
                    {/* Journal: J-20251222-01 */}
                    {!endDateReached && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-theme-silver-light">{t('giveaways.availableSlots')}:</span>
                          <span className="font-semibold text-theme-silver">
                            {giveaway.availableSlots}/{giveaway.totalSlots}
                          </span>
                        </div>
                        {/* Progress bar — slots filled */}
                        {Number(giveaway.totalSlots) > 0 && (
                          <div
                            className="h-2 rounded-full bg-theme-gunmetal/80 border border-theme-silver/10 overflow-hidden"
                            role="progressbar"
                            aria-valuenow={Math.min(100, Math.round(((Number(giveaway.totalSlots) - Number(giveaway.availableSlots || 0)) / Number(giveaway.totalSlots)) * 100))}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label="Giveaway fill progress"
                          >
                            <div
                              className="h-full rounded-full bg-green-600 transition-all duration-500"
                              style={{
                                width: `${Math.min(100, Math.max(0, ((Number(giveaway.totalSlots) - Number(giveaway.availableSlots || 0)) / Number(giveaway.totalSlots)) * 100))}%`,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-theme-silver-light">Your Entries:</span>
                      <span className="font-semibold text-custom-orange">
                        {userEntriesCount}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-theme-silver-light">{t('admin.value')}:</span>
                      <span className={`font-semibold text-theme-silver ${isClassified ? 'blur-md select-none' : ''}`}>
                        ${Number(giveaway?.value ?? 0).toLocaleString()}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-theme-silver-light">Early Access:</span>
                      <CountdownTimer 
                        endDate={giveaway.earlyAccessDate || calculateEarlyAccessDate(giveaway.startDate)}
                        currentTime={currentTime}
                        variant="inline"
                        showIcon={true}
                        expiredText="Available Now"
                      />
                    </div>

                    {!isSubscriber && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-theme-silver-light">Available:</span>
                        <CountdownTimer 
                          endDate={giveaway.startDate}
                          currentTime={currentTime}
                          variant="inline"
                          showIcon={true}
                          expiredText="Available Now"
                        />
                      </div>
                    )}

                    {/* Enhanced Date Display */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-theme-silver-light">Start Date:</span>
                      <span className="font-semibold text-theme-silver">
                        {formatDate(giveaway.startDate)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-theme-silver-light">End Date:</span>
                      <span className="font-semibold text-theme-silver">
                        {formatDate(giveaway.endDate)} or until sold out
                      </span>
                    </div>
                  </div>

                  {/* Inline Slot Grid - Show for active giveaways (only if not sold out and end date not reached) */}
                  {!past && canEnter && !isSoldOut && !giveaway.winner && giveaway.status !== 'completed' && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-theme-silver mb-2">
                        {t('giveaways.availableSlots')}{' '}
                        <span className="text-xs text-theme-silver-light">(numbers shown — red = reserved)</span>
                      </h4>
                      <GiveawaySlotGrid
                        giveaway={giveaway}
                        user={user}
                        serverEntries={serverEntries}
                        variant="inline"
                        maxHeight="200px"
                        showEntryButton={false} // Use the card-level button instead (avoid duplicate "Enter Giveaway")
                        readOnly={false} // Explicitly enable slot selection for active giveaways
                        winningSlot={winningSlot} // Highlight winning slot in gold
                        isSoldOut={false} // Active giveaways don't show "winner" in legend
                        hideWinnerUntil={giveaway?.hideWinnerUntil} // AI CONTEXT: Hide winner until timestamp (J-20260112-01)
                        onOpenModal={() => openGiveawayModal(giveaway)}
                        onEnterGiveaway={async (giveawayId, selectedSlots, userId) => {
                          // Check credits before entry
                          const credits = getUserCredits(userId);
                          const creditKey = `${String(giveaway?.tier || 'Bronze').toLowerCase()}Credits`;
                          const availableCredits = credits[creditKey] || 0;
                          const requiredCredits = selectedSlots.length;
                          const tierName = String(giveaway?.tier || 'Bronze');

                          if (availableCredits < requiredCredits) {
                            // Show insufficient credits modal
                            const missingCredits = requiredCredits - availableCredits;
                            setInsufficientCreditsInfo({
                              availableCredits,
                              selectedSlotsCount: requiredCredits,
                              missingCredits,
                              tier: tierName,
                              giveaway: giveaway,
                              selectedSlots: selectedSlots
                            });
                            return false; // Prevent entry
                          }

                          // User has sufficient credits, proceed with entry
                          const success = await enterGiveaway(giveawayId, selectedSlots, userId);
                          
                          // AI CONTEXT: bug-fix - Refresh server entries after successful entry
                          // Reasoning: Newly entered slots need to be highlighted in green immediately
                          // Journal: J-20251226-02
                          if (success && isApiConfigured()) {
                            // Refresh entries to show newly entered slots highlighted in green
                            await refreshServerEntries();
                          }
                          
                          return success;
                        }}
                      />
                      
                      {/* AI CONTEXT: ux - View Slot Grid button at bottom of active giveaway card
                          Reasoning: Opens modal with larger slot grid for easier selection and entry
                          Journal: J-20260108-05 */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openGiveawayModal(giveaway);
                        }}
                        className="btn-primary w-full mt-4"
                      >
                        View Full Slot Grid
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty state when no visible giveaways */}
        {displayedGiveaways.length === 0 && (
          <div className="text-center py-12">
            <Gift className="h-12 w-12 text-theme-silver mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-theme-silver mb-2">
              No {listTab === 'all' ? '' : listTab === 'active' ? 'active ' : 'past '}giveaways
            </h3>
            <p className="text-theme-silver-light">Please check back later{listTab === 'active' || listTab === 'all' ? ' or subscribe for early access.' : '.'}</p>
          </div>
        )}

        {/* Modals */}
        {GiveawayEntryModal}
        {insufficientCreditsInfo && <InsufficientCreditsModal />}
      </div>
    </div>
  );
};

export default Giveaways; 